import * as React from 'react'

import { action, makeObservable } from 'mobx'
import { observer } from 'mobx-react'

import { ILWFCColumn } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ILocationTransferPair from '~/client/src/shared/models/ILocationTransferPair'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Workflow from '~/client/src/shared/models/Workflow'
import { NOOP } from '~/client/src/shared/utils/noop'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import { MaterialsDataKeys } from '../MaterialsDataKeys'
import { BaseDataCellCloseContext } from './BaseDataCell/BaseDataCell'
import BaseDataCellTable from './BaseDataCell/BaseDataCellTable'
import CurrentLocationStatusIcon from './CurrentLocationStatusIcon'

// localization: translated

interface IProps {
  isTransferEnabled: boolean
  plannedDeliveryLocationId: string
  plannedInstallLocationId: string
  transferPairs: ILocationTransferPair[]
  selectedLocationsMap: Map<string, ILocationTransferPair>
  iconElement: JSX.Element
  onTransferIdClick(workflow: Workflow): void

  locationRenderer?(location: LocationBase): JSX.Element
}

const MULTIPLE_TRANSFERS_ROW_HEIGHT = 32
const DEFAULT_ROW_HEIGHT = 40

@observer
export default class CurrentLocationCellTable extends React.Component<IProps> {
  public static contextType = BaseDataCellCloseContext
  declare public context: React.ContextType<typeof BaseDataCellCloseContext>

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    return (
      <BaseDataCellTable<ILocationTransferPair>
        items={this.props.transferPairs}
        columns={this.columns}
        getRowHeightByItem={this.getRowHeightByTransferPair}
        getCellContentByDataKey={this.getCellContentByDataKey}
        isFooterHidden
        getFooterTitleByDataKey={NOOP}
        onIdClick={NOOP}
        onSelectAll={this.selectAllLocations}
        onClearAll={this.clearAllLocations}
      />
    )
  }

  private getCellContentByDataKey = (
    dataKey: string,
    transferPair: ILocationTransferPair,
  ): JSX.Element => {
    switch (dataKey) {
      case MaterialsDataKeys.CHECKBOX:
        return (
          <span className="tw:flex tw:justify-center tw:w-full">
            <Checkbox
              checked={transferPair.isSelected}
              onCheckedChange={() => this.toggleSelectedLocation(transferPair)}
            />
          </span>
        )
      case MaterialsDataKeys.PLANNED_QUANTITY:
        return (
          <span className="text large end ellipsis">
            {transferPair.quantity}
          </span>
        )
      case MaterialsDataKeys.CURRENT_MATERIAL_LOCATION:
        return this.renderLocation(transferPair)
      case MaterialsDataKeys.MATERIAL_TRANSFER_ID:
        return this.renderTransferIds(transferPair)
      case MaterialsDataKeys.STATUS:
        return this.renderTransferStatuses(transferPair)
      default:
        return null
    }
  }

  private renderLocation = (
    transferPair: ILocationTransferPair,
  ): JSX.Element => {
    const {
      locationRenderer,
      plannedDeliveryLocationId,
      plannedInstallLocationId,
      iconElement,
    } = this.props

    if (!transferPair.location?.id) {
      return locationRenderer(null)
    }

    return (
      <div className="col overflow-hidden">
        <div className="row py2">
          {locationRenderer(transferPair.location)}
          <CurrentLocationStatusIcon
            transferPairs={[transferPair]}
            plannedDeliveryLocationId={plannedDeliveryLocationId}
            plannedInstallLocationId={plannedInstallLocationId}
            iconElement={iconElement}
          />
        </div>
      </div>
    )
  }

  private renderTransferIds = (
    transferPair: ILocationTransferPair,
  ): JSX.Element => {
    if (!transferPair.materialTransfers?.length) {
      return <span className="text large end ellipsis">{NO_VALUE}</span>
    }

    return (
      <div className="col overflow-hidden">
        {transferPair.materialTransfers.map(form => (
          <span
            key={form.id}
            className="row x-end py4 text large ellipsis pointer form-id-link underline-hover"
            onClick={this.onTransferIdClick.bind(null, form)}
          >
            {form.code}
          </span>
        ))}
      </div>
    )
  }

  private renderTransferStatuses = (
    transferPair: ILocationTransferPair,
  ): JSX.Element => {
    if (!transferPair.materialTransfers?.length) {
      return <span className="text large end ellipsis">{NO_VALUE}</span>
    }

    return (
      <div className="col overflow-hidden">
        {transferPair.materialTransfers.map(form => (
          <div key={form.id} className="row x-end py4">
            <StatusBadge
              className="no-grow tw:!flex-none"
              status={form.status}
              workflowStepLevel={form.workflowStepLevel}
              isLate={form.isLate}
            />
          </div>
        ))}
      </div>
    )
  }

  private get columns(): ILWFCColumn[] {
    return [
      ...(this.props.isTransferEnabled
        ? [
            {
              dataKey: MaterialsDataKeys.CHECKBOX,
              width: 30,
            },
          ]
        : []),
      {
        label: Localization.translator.currentLocation,
        dataKey: MaterialsDataKeys.CURRENT_MATERIAL_LOCATION,
        width: 180,
      },
      {
        label: Localization.translator.quantity,
        dataKey: MaterialsDataKeys.PLANNED_QUANTITY,
        width: 80,
      },
      {
        label: Localization.translator.materialTransferId,
        dataKey: MaterialsDataKeys.MATERIAL_TRANSFER_ID,
        width: 140,
      },
      {
        label: Localization.translator.status,
        dataKey: MaterialsDataKeys.STATUS,
        width: 125,
      },
    ]
  }

  private getRowHeightByTransferPair = (
    transferPair: ILocationTransferPair,
  ): number => {
    if (!transferPair) {
      return 0
    }
    if (
      !transferPair.materialTransfers?.length ||
      transferPair.materialTransfers?.length === 1
    ) {
      return DEFAULT_ROW_HEIGHT
    }
    return transferPair.materialTransfers.length * MULTIPLE_TRANSFERS_ROW_HEIGHT
  }

  @action
  private toggleSelectedLocation(transferPair: ILocationTransferPair) {
    const { selectedLocationsMap } = this.props
    if (selectedLocationsMap.has(transferPair.location.id)) {
      selectedLocationsMap.delete(transferPair.location.id)
    } else {
      selectedLocationsMap.set(transferPair.location.id, transferPair)
    }
  }

  @action.bound
  private selectAllLocations() {
    const { transferPairs, selectedLocationsMap } = this.props
    transferPairs.forEach(tp => selectedLocationsMap.set(tp.location.id, tp))
  }

  @action.bound
  private clearAllLocations() {
    this.props.selectedLocationsMap.clear()
  }

  private onTransferIdClick = (workflow: Workflow) => {
    this.context()
    this.props.onTransferIdClick(workflow)
  }
}
